import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useEntitlements } from '../../contexts/EntitlementsContext';

/**
 * What a Free account sees when the server refuses something with 402 PLAN_LIMIT.
 *
 * Mounted once, globally, and driven entirely by the plan-limit channel, so a feature added
 * next week gets this dialog by returning the right status code, with no UI work at all.
 *
 * The copy names the specific limit that was hit rather than saying "upgrade required": someone
 * who has just been stopped mid-action needs to know what stopped them, and "you already have a
 * live flow" is actionable in a way that "payment required" is not.
 */
const LIMIT_COPY = {
  liveFlows: {
    title: 'You already have a flow live',
    body: 'The Free plan keeps one flow live on WhatsApp at a time. You can deprecate the one '
      + 'you have to free the slot, though WhatsApp makes that permanent, or upgrade to Team to '
      + 'run as many as you like.',
  },
  // Free and Team both connect one number; only Business has several, and Business is not sold
  // in the app, so on Team there is nothing to upgrade to here and the dialog says so plainly.
  numbers: {
    title: 'One WhatsApp number on your plan',
    body: 'Your plan connects one WhatsApp number. You can change which one in Settings, under '
      + 'Your number. Several numbers come with Business.',
  },
  environments: {
    title: 'One environment on Free',
    body: 'The Free plan has one environment, with one WhatsApp number. Team adds as many '
      + 'environments as you need.',
  },
  workspaces: {
    title: 'One workspace on Free',
    body: 'The Free plan includes one workspace. Upgrade it to Team and you can add another.',
  },
  deliveries: {
    title: 'Delivering answers is a Team feature',
    body: 'Free collects unlimited submissions and shows them on your Submissions page, with CSV '
      + 'export, and can reply to the customer on WhatsApp. Sending them on to email, a webhook '
      + 'or Google Sheets is part of Team.',
  },
  intentTriggers: {
    title: 'Automatic triggers are a Team feature',
    body: 'Free sends your flow when someone types your keyword. Team reads what they meant, so '
      + 'the form goes out whether they wrote "book" or "can I get an appointment".',
  },
  booking: {
    title: 'Live booking is a Team feature',
    body: 'Team lets a customer pick a real slot inside the flow and lands it in your schedule.',
  },
};

const PRO_INCLUDES = [
  'Unlimited live flows and triggers',
  'Answers delivered to email, a webhook or Google Sheets',
  'Live booking inside the flow',
  'The "Built with FlowGate" line switched off',
];

const PlanLimitDialog = () => {
  const { planLimit, dismissPlanLimit, isFree: accountIsFree } = useEntitlements();
  // The refusal says which plan refused it; Team is only worth offering to somebody on Free
  const isFree = planLimit?.plan ? planLimit.plan === 'FREE' : accountIsFree;
  const navigate = useNavigate();

  const copy = LIMIT_COPY[planLimit?.limit] ?? {
    title: 'That needs the Team plan',
    body: planLimit?.message ?? 'Upgrade to Team to unlock this.',
  };

  const upgrade = () => {
    dismissPlanLimit();
    navigate('/settings?tab=billing');
  };

  return (
    <Dialog open={Boolean(planLimit)} onClose={dismissPlanLimit} maxWidth="xs" fullWidth>
      <DialogTitle>{copy.title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{copy.body}</DialogContentText>
        {isFree && (
        <List dense sx={{ mt: 1 }}>
          {PRO_INCLUDES.map((item) => (
            <ListItem key={item} disableGutters>
              <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
              <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2' }} />
            </ListItem>
          ))}
        </List>
        )}
      </DialogContent>
      <DialogActions>
        {isFree ? (
          <>
            <Button onClick={dismissPlanLimit}>Not now</Button>
            <Button onClick={upgrade} variant="contained">See Team</Button>
          </>
        ) : (
          <Button onClick={dismissPlanLimit} variant="contained">OK</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PlanLimitDialog;
